<template>
  <div class="category">
    <CategoryBreadcrumb :language="language" />
    <div class="category-information flex-col md:flex-row">
      <ImageViewer
        v-if="productMime"
        :mime="productMime"
        :language="language"
      />
      <img
        v-else-if="categoryImage?.length"
        class="category-image"
        :src="categoryImage"
      />
      <img
        v-else-if="!categoryImage?.length && tempCategoryImage.length"
        class="category-image"
        :src="tempCategoryImage"
      />
      <content-placeholders
        class="category-image"
        v-else-if="!categoryImage?.length && hasProduct"
      >
        <content-placeholders-img />
      </content-placeholders>

      <div
        class="category-details flex flex-col text-center md:block md:text-left"
      >
        <h1>{{ categoryDetails?.name[language] }}</h1>
        <div class="product-count-display">
          <h4 v-if="!categoryDetails?.products" class="break-words">
            Loading products from this category...
          </h4>
          <h4 v-else>
            {{ categoryDetails?.products?.total || 0 }}
            {{
              categoryDetails?.products?.total === 1 ? "product" : "products"
            }}
          </h4>
        </div>

        <!-- Product purpose -->
        <div class="mt-6 text-left" v-if="extraInfo?.productPurpose">
          <h4>
            {{
              extraInfo.productPurpose.header[language] ||
              extraInfo.productPurpose.header.en
            }}
          </h4>
          <ul class="list-disc px-4 text-left">
            <li
              v-for="detail in extraInfo.productPurpose.text[language] ||
              extraInfo.productPurpose.header.en"
              :key="detail"
            >
              {{ detail }}
            </li>
          </ul>
        </div>

        <!-- Specifications -->
        <div class="mt-6 text-left" v-if="extraInfo?.specifications">
          <h4>
            {{
              extraInfo.specifications.header[language] ||
              extraInfo.specifications.header.en
            }}
          </h4>
          <ul class="list-disc px-4 text-left">
            <li
              v-for="detail in extraInfo.specifications.text[language]"
              :key="detail"
            >
              {{ detail }}
            </li>
          </ul>
        </div>

        <!-- Certification -->
        <div class="mt-6 text-left" v-if="extraInfo?.productCertification">
          <h4>
            {{
              extraInfo.productCertification?.header?.[language] ||
              extraInfo.productCertification?.header?.en
            }}
          </h4>
          <ul class="list-none text-left">
            <li
              v-for="(attachment, index) of extraInfo.productCertification
                .imageUrl"
              :key="index"
            >
              <a
                :href="attachment"
                target="_blank"
                class="break-all flex items-center gap-2"
              >
                <div class="flex items-center gap-3 py-3 px-4 border">
                  <box-icon class="w-12 md:w-24" name="download"></box-icon>
                  <h5 class="m-0 line-clamp-2">{{ attachment }}</h5>
                </div>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="category-products-container" v-if="!getSubcategories().length">
      <ProductList
        v-model="page"
        :productChunks="filteredProductChunks || productChunks"
        :isLoading="isFetching"
        :language="language"
        :show-search="false"
        @page-change="handlePageChange"
        @search="handleSearch"
      />
    </div>

    <CategoryList
      v-else
      :language="language"
      :categoryList="getSubcategories()"
    />
  </div>
</template>
<script>
import { getCategoryPath } from "@/helpers/categories";
import CategoryList from "@/components/Categories/CategoryList.vue";
import ProductList from "@/components/Products/ProductList.vue";
import CategoryBreadcrumb from "@/components/Categories/CategoryBreadcrumb.vue";
import ProductSearch from "@/components/Products/ProductPage/ProductSearch.vue";
import getCategoryExtraInfo from "@/helpers/getCategoryExtraInfo";
import ImageViewer from "@/components/Products/ProductPage/ImageViewer.vue";

export default {
  name: "CategoryProductPage",
  components: {
    CategoryList,
    ProductList,
    CategoryBreadcrumb,
    ProductSearch,
    ImageViewer,
  },
  data() {
    return {
      tempCategoryImage: "",
      hasProduct: true,
      searchString: "",
      page: 0,
    };
  },
  methods: {
    getSubcategories() {
      const identifier = this.$route.params.categoryIdentifier;
      const cat = this.categoryPath?.find(
        (category) => category.identifier == identifier
      );

      if (!cat) return [];

      return cat?.subcategories || [];
    },
    getTempCategoryImage() {
      let image = "";
      let i = 0,
        j = 0;

      if (!Object.keys(this.productChunks)?.length) {
        this.tempCategoryImage = "";
        this.hasProduct = false;
        return;
      }

      do {
        const products = this.productChunks?.[i]?.products?.[j];
        image = products?.mime[0].mimeSource[this.language];

        if (j === products?.length - 1) {
          i++;
          j = 0;
        } else {
          j++;
        }
      } while (!image?.length && i < this.productChunks?.length);

      this.tempCategoryImage = image;
    },
    handlePageChange(e) {
      if (
        !this.filteredProductChunks &&
        !this.productChunks[e.current].loaded
      ) {
        this.$store.dispatch("fetchCategoryProductChunk", {
          identifier: this.routeIdentifier,
          chunk: e.current,
        });
        return;
      }

      if (
        this.filteredProductChunks &&
        !this.filteredProductChunks?.[e.current].loaded
      ) {
        this.$store.dispatch("filterCategoryProducts", {
          identifier: this.routeIdentifier,
          searchString: this.searchString,
          language: this.language,
          chunk: e.current,
        });
      }
    },
    handleSearch(e) {
      this.searchString = e;
      const currentRoute = this.$route;

      const searchRoute = {
        name: "category",
        ...(e.length
          ? {
              query: {
                ...(this.$route.query || {}),
                search: e,
              },
            }
          : {}),
      };

      const resolvedRoute = this.$router.resolve(searchRoute);

      if (!currentRoute.query.search) {
        this.$router.push(searchRoute);
      } else if (currentRoute.fullPath !== resolvedRoute.route.fullPath) {
        this.$router.replace(searchRoute);
      }

      this.$store
        .dispatch("filterCategoryProducts", {
          identifier: this.routeIdentifier,
          searchString: e,
          language: this.language,
        })
        .then(() => {
          this.page = 0;
        });
    },
  },
  computed: {
    routeIdentifier() {
      return this.$route.params.categoryIdentifier;
    },
    categoryDetails() {
      const identifier = this.$route.params.categoryIdentifier;
      const categoryList = this.$store.getters.categoryList;
      return categoryList.find((cat) => cat.identifier == identifier);
    },
    categoryPath() {
      const identifier = this.$route.params.categoryIdentifier;
      const path = getCategoryPath(
        identifier,
        this.language,
        this.$store.getters.categoryTree
      );

      return path;
    },
    language() {
      return this.$route.query.lang || "en";
    },
    filteredProductChunks() {
      const categoryList = this.$store.getters.categoryList;
      const category = categoryList.find(
        (cat) => cat.identifier === this.routeIdentifier
      );

      return category?.filteredProducts?.chunks;
    },
    productChunks() {
      const categoryList = this.$store.getters.categoryList;
      const category = categoryList.find(
        (cat) => cat.identifier === this.routeIdentifier
      );

      return category?.products?.chunks;
    },
    isFetching() {
      return this.$store.getters.isFetchingCategoryProducts;
    },
    categoryImage() {
      return this.categoryDetails?.www?.[0]?.imageUrl?.[this.language];
    },
    productMime() {
      if (!this.productChunks) return null;

      if (!Object.keys(this.productChunks)?.length) {
        return null;
      }

      return this.productChunks[0]?.products?.[0]?.mime;
    },
    extraInfo() {
      return getCategoryExtraInfo(this.categoryDetails, this.language);
    },
  },
  beforeRouteUpdate(to, from, next) {
    this.$store
      .dispatch("fetchInitialProducts", {
        identifier: to.params.categoryIdentifier,
      })
      .then(() => {
        this.page = 0;
        this.getTempCategoryImage();
      });

    next();
  },
  async mounted() {
    await this.$store.dispatch("fetchInitialProducts", {
      identifier: this.routeIdentifier,
    });

    this.getTempCategoryImage();
  },
};
</script>

<style>
.category-products-container {
  margin-top: 4rem;
}

.category-information {
  display: flex;
  gap: 2rem;
  padding: 3rem 1rem;
}

.category-image {
  max-height: 300px;
  width: 300px;
  object-fit: contain;
}

.product-count-display {
  color: white;
  padding: 8px 1rem;
  text-align: center;
  width: max-content;
  background: var(--rofix-black);
}

.product-count-display h4 {
  margin: 0;
}
</style>
